import { morphSection } from '@theme/section-renderer';

/**
 * Warranty guardian — keeps warranty line items in sync with Engine units.
 *
 * Rules enforced on every cart update (and once on page load):
 * - warranty units may never exceed Engine units; excess is reduced,
 *   dropping "orphaned" warranty lines first (whose Covers SKU has no
 *   matching engine left in the cart).
 * - a cart with zero Engine units gets all warranty lines removed.
 *
 * Also handles the one-click "top up coverage" button rendered by
 * snippets/cart-warranty-upsell.liquid ([data-warranty-topup]).
 */

const ENGINE_PRODUCT_TYPE = 'Engine';
const WARRANTY_SKU = 'warranty';
const COVERS_PROPERTY = 'Covers';

let busy = false;
let debounceTimer;

const jsonConfig = (body) => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  body: JSON.stringify(body),
});

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);
}

async function fetchCart() {
  const response = await fetch('/cart.js');
  return response.json();
}

/**
 * @param {{items: Array<Object>}} cart
 */
function tally(cart) {
  let engineUnits = 0;
  let warrantyUnits = 0;
  const engineBySku = new Map();
  const warrantyLines = [];

  for (const item of cart.items) {
    if ((item.sku || '').toLowerCase() === WARRANTY_SKU) {
      warrantyUnits += item.quantity;
      warrantyLines.push(item);
    } else if (item.product_type === ENGINE_PRODUCT_TYPE) {
      engineUnits += item.quantity;
      engineBySku.set(item.sku, (engineBySku.get(item.sku) || 0) + item.quantity);
    }
  }

  return { engineUnits, engineBySku, warrantyUnits, warrantyLines };
}

function coveredSku(line) {
  return (line.properties && line.properties[COVERS_PROPERTY]) || null;
}

function dispatchCartUpdate(detailData) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      detail: {
        resource: detailData.resource,
        sourceId: 'warranty-guardian',
        data: { source: 'warranty-guardian', sections: detailData.sections, itemCount: detailData.itemCount },
      },
    })
  );
}

/** Morph updated section HTML in place without dispatching cart:update
 * (used on page load so an auto-open cart drawer stays closed). */
function morphSections(sections) {
  if (!sections) return;
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (!(el instanceof HTMLElement)) continue;
    const sectionId = el.dataset.sectionId;
    if (sectionId && sections[sectionId]) {
      morphSection(sectionId, sections[sectionId], { mode: el.dataset.drawer !== undefined ? 'hydration' : 'full' });
    }
  }
}

/**
 * Reduce warranty quantity down to the engine-unit count (removes all
 * warranty lines when no engines remain).
 * @param {boolean} silent - morph sections directly instead of dispatching cart:update
 */
async function reconcile(silent = false) {
  if (busy) return;
  busy = true;

  try {
    const cart = await fetchCart();
    const { engineUnits, engineBySku, warrantyUnits, warrantyLines } = tally(cart);
    if (warrantyUnits <= engineUnits) return;

    let excess = warrantyUnits - engineUnits;

    // Drop orphaned coverage first, then untagged lines, then the rest.
    const priority = (line) => {
      const sku = coveredSku(line);
      if (sku && !engineBySku.has(sku)) return 2;
      if (!sku) return 1;
      return 0;
    };
    const ordered = [...warrantyLines].sort((a, b) => priority(b) - priority(a));

    const updates = {};
    for (const line of ordered) {
      if (excess <= 0) break;
      const cut = Math.min(line.quantity, excess);
      updates[line.key] = line.quantity - cut;
      excess -= cut;
    }

    const response = await fetch(
      Theme.routes.cart_update_url,
      jsonConfig({ updates, sections: cartSectionIds().join(',') })
    );
    const updatedCart = await response.json();

    if (updatedCart.status) {
      console.error('[warranty-guardian] reconcile failed:', updatedCart.message || updatedCart);
      return;
    }

    if (silent) {
      morphSections(updatedCart.sections);
    } else {
      dispatchCartUpdate({ resource: updatedCart, sections: updatedCart.sections, itemCount: updatedCart.item_count });
    }
  } catch (error) {
    console.error('[warranty-guardian]', error);
  } finally {
    busy = false;
  }
}

/** One-click top-up: add warranty units until they match engine units,
 * tagging each added line with Covers = the engine SKU it protects. */
async function topUp(button) {
  if (busy) return;
  busy = true;
  button.setAttribute('disabled', '');

  try {
    const warrantyVariantId = Number(button.dataset.warrantyVariantId);
    if (!warrantyVariantId) return;

    const cart = await fetchCart();
    const { engineUnits, engineBySku, warrantyUnits, warrantyLines } = tally(cart);
    let deficit = engineUnits - warrantyUnits;
    if (deficit <= 0) return;

    const covered = new Map();
    for (const line of warrantyLines) {
      const sku = coveredSku(line);
      if (sku) covered.set(sku, (covered.get(sku) || 0) + line.quantity);
    }

    const items = [];
    for (const [sku, quantity] of engineBySku) {
      if (deficit <= 0) break;
      const need = Math.min(Math.max(0, quantity - (covered.get(sku) || 0)), deficit);
      if (need > 0) {
        items.push({ id: warrantyVariantId, quantity: need, properties: { [COVERS_PROPERTY]: sku } });
        deficit -= need;
      }
    }
    // Safety net: per-SKU shortfalls always cover the aggregate deficit, but
    // never leave the cart short if coverage tags are inconsistent.
    if (deficit > 0) items.push({ id: warrantyVariantId, quantity: deficit });

    const response = await fetch(
      Theme.routes.cart_add_url,
      jsonConfig({ items, sections: cartSectionIds().join(',') })
    );
    const result = await response.json();

    if (result.status) {
      console.error('[warranty-guardian] top-up failed:', result.message || result);
      return;
    }

    dispatchCartUpdate({ sections: result.sections });
  } catch (error) {
    console.error('[warranty-guardian]', error);
  } finally {
    busy = false;
    button.removeAttribute('disabled');
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('[data-warranty-topup]');
  if (button instanceof HTMLElement) topUp(button);
});

document.addEventListener('cart:update', (event) => {
  const source = event.detail?.data?.source;
  if (source === 'warranty-guardian') return;

  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => reconcile(), 250);
});

reconcile(true);
